import { ThemeProvider } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { LoginCallback, SecureRoute, useOktaAuth } from '@okta/okta-react';
import {
  Header,
  NavigationList,
  Page,
  PageContentWrapper,
  PageHeaderWrapper,
  PageMainWrapper,
  PageSidebarWrapper,
  SecurityWrapper,
  helixLight as theme,
} from '@signanthealth/modular-ui';
import '@signanthealth/modular-ui/styles/base.css';
import '@signanthealth/modular-ui/styles/fontawesome-regular.css';
import '@signanthealth/modular-ui/styles/fontawesome-solid.css';
import '@signanthealth/modular-ui/styles/fontawesome.css';
import '@signanthealth/modular-ui/styles/normalize.css';
import { useEffect, useState } from 'react';
import { Route, BrowserRouter as Router, Switch } from 'react-router-dom';

import { FooterPage } from './components/common/footer/FooterPage';
import { ProductContent } from './components/features/product/ProductContent';
import config from './config';
import { useProductNavigation } from './hooks/useProductNavigation';
import { oktaAuth } from './services/oktaInitialization';
import { useAppSelector } from './store/hooks';
import { selectSelectedProduct, selectSelectedProductTabs } from './store/slices/productSlice';
import type { Product, Tab } from './types/common.types';
import { getUserInitials } from './utils/oktaAuthUtils';

interface ProductContentProps {
  selectedProduct?: Product;
  tabs: Tab[];
}
const ApplicationContent = ({ selectedProduct, tabs }: ProductContentProps) => {
  return <>{selectedProduct ? <ProductContent product={selectedProduct} tabs={tabs} /> : null}</>;
};

export const App = () => {
  const { authState } = useOktaAuth();
  const [userInitials, setUserInitials] = useState('');

  useEffect(() => {
    const initials = getUserInitials(authState);
    setUserInitials(initials);
  }, [authState]);

  const { navigationItems } = useProductNavigation();
  const selectedProduct = useAppSelector(selectSelectedProduct);
  const selectedProductTabs = useAppSelector(selectSelectedProductTabs);

  const backgroundColor =
    theme.palette.mode === 'light' ? '#F6F6F6' : theme.palette.background.default;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Page data-testid="page">
        <PageHeaderWrapper data-testid="page-header-wrapper">
          <Header
            appName={config.APP_NAME}
            brandName={config.BRAND_NAME}
            homeUrl={config.APPLICATION_PATH}
            trademarkSymbol="®"
            userInitials={userInitials}
            logout={() => {
              localStorage.clear();
            }}
          />
        </PageHeaderWrapper>
        <PageContentWrapper data-testid="page-content-wrapper">
          <PageSidebarWrapper sidebarWidth="350px">
            <NavigationList navigationItems={navigationItems} />
          </PageSidebarWrapper>
          <PageMainWrapper
            data-testid="page-main-wrapper"
            sidebarWidth="350px"
            sx={{ padding: '24px', backgroundColor }}
          >
            <ApplicationContent selectedProduct={selectedProduct} tabs={selectedProductTabs} />
          </PageMainWrapper>
        </PageContentWrapper>
        <FooterPage />
      </Page>
    </ThemeProvider>
  );
};

const AppWrapper = () => {
  return (
    <Router basename={config.APPLICATION_PATH}>
      <SecurityWrapper
        oktaAuth={oktaAuth}
        applicationPath={config.APPLICATION_PATH}
        enableGlobalLogout
      >
        <Switch>
          <Route exact path="/login/callback" component={LoginCallback} />
          <SecureRoute exact path="/" component={App} />
        </Switch>
      </SecurityWrapper>
    </Router>
  );
};

export default AppWrapper;
